import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import PropertyCard, { resolveTier } from '../components/PropertyCard';
import ForYourTasteRow from '../components/ForYourTasteRow';
import RecentlyViewedRail from '../components/RecentlyViewedRail';
import { propertiesAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Filter, X, Bell, Loader2, Pencil } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import SEO from '../components/SEO';
import { Helmet } from 'react-helmet-async';
import { groupedTypesForMode } from '../utils/propertyTypes';
import { useMarket } from '../hooks/useMarket';
import LocationCombobox from '../components/LocationCombobox';
import PropertyMap from '../components/PropertyMap';
import AISearchBar from '../components/AISearchBar';
import { Map as MapIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ListingsPage = ({ countryOverride } = {}) => {
  const market = useMarket(countryOverride);
  const isUK = market.code === 'GB';
  const isUS = market.code === 'US';
  const energyLabel = isUK ? 'EPC' : isUS ? 'Energy' : 'BER';
  const energyRegulator = isUK
    ? 'the government EPC register'
    : isUS
      ? 'the DOE Home Energy Score registry'
      : 'the official SEAI register';
  const [searchParams] = useSearchParams();
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savingSearch, setSavingSearch] = useState(false);
  const { isAuthenticated } = useAuth();

  // Filter states
  const [filters, setFilters] = useState({
    type: searchParams.get('type') || 'all',
    location: searchParams.get('location') || searchParams.get('q') || '',
    propertyType: searchParams.get('propertyType') || 'all',
    isNewHome: searchParams.get('newHome') === '1',
    minPrice: parseInt(searchParams.get('priceMin') || '0', 10) || 0,
    maxPrice: parseInt(searchParams.get('priceMax') || '1000000', 10) || 1000000,
    bedrooms: searchParams.get('bedsMin') || 'all',
    bathrooms: searchParams.get('bathsMin') || 'all',
    berVerified: false,
    berMin: (searchParams.get('berMin') || '').toUpperCase() || '',
  });
  // Optional map-drawn polygon — when set, sent to the API as `polygon=`
  // to restrict results to inside the shape. History stack powers the
  // Undo control so a fresh trace overrides but the user can pop back
  // to their previous shape without wiping any of their filters.
  const [polygon, setPolygon] = useState([]);
  const [polygonHistory, setPolygonHistory] = useState([]);
  const pushPolygon = useCallback((next) => {
    setPolygon((prev) => {
      if (prev.length >= 3) setPolygonHistory((h) => [...h, prev]);
      return next;
    });
  }, []);
  const undoPolygon = useCallback(() => {
    setPolygonHistory((h) => {
      if (!h.length) { setPolygon([]); return h; }
      setPolygon(h[h.length - 1]);
      return h.slice(0, -1);
    });
  }, []);
  // Auto-open the map on ALL viewports so buyers land on a
  // Rightmove/Daft-style split view (list + map).  Previously we
  // kept it collapsed on mobile which made the map feel absent.
  const [showMap, setShowMap] = useState(true);

  // Location dropdown source — same shape LocationCombobox expects on
  // the homepage hero (`{counties: [{county, total, areas: [{area,count}]}]}`).
  const [areas, setAreas] = useState([]);
  useEffect(() => {
    const type = filters.type !== 'all' ? filters.type : 'sale';
    fetch(
      `${BACKEND_URL}/api/properties/areas?type=${type}` +
      `&country=${encodeURIComponent(market.name || 'Ireland')}`,
    )
      .then((r) => r.ok ? r.json() : { counties: [] })
      .then((d) => setAreas(d?.counties || []))
      .catch(() => setAreas([]));
  }, [filters.type, market.name]);

  useEffect(() => {
    const fetchProperties = async () => {
      setLoading(true);
      try {
        const apiFilters = {};

        // Market scoping — always filter to the active market so /uk/buy
        // never surfaces Dublin homes and vice-versa.
        if (market.name) apiFilters.country = market.name;

        if (filters.type !== 'all') apiFilters.type = filters.type;
        if (filters.location) apiFilters.location = filters.location;
        if (filters.propertyType !== 'all') apiFilters.propertyType = filters.propertyType;
        if (filters.isNewHome) apiFilters.isNewHome = true;
        if (filters.minPrice > 0) apiFilters.minPrice = filters.minPrice;
        if (filters.maxPrice < 1000000) apiFilters.maxPrice = filters.maxPrice;
        if (filters.bedrooms !== 'all') apiFilters.bedrooms = parseInt(filters.bedrooms);
        if (filters.bathrooms !== 'all') apiFilters.bathrooms = parseInt(filters.bathrooms);
        if (filters.berVerified) apiFilters.ber_verified = true;
        if (polygon && polygon.length >= 3) {
          apiFilters.polygon = polygon.map((p) => `${p[0]},${p[1]}`).join(';');
        }

        const properties = await propertiesAPI.getAll(apiFilters);
        setFilteredProperties(properties);
      } catch (error) {
        console.error('Error fetching properties:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, [filters, market.name, polygon]);

  const clearFilters = () => {
    setFilters({
      type: 'all',
      location: '',
      propertyType: 'all',
      isNewHome: false,
      minPrice: 0,
      maxPrice: 1000000,
      bedrooms: 'all',
      bathrooms: 'all',
      berVerified: false,
    });
  };

  const saveSearch = async () => {
    if (!isAuthenticated) {
      toast({
        title: 'Sign in to save searches',
        description: 'Get email alerts when new properties match your filters.',
      });
      return;
    }
    const name = window.prompt('Name this saved search', filters.location || 'My search');
    if (!name) return;

    setSavingSearch(true);
    try {
      const payload = {
        name,
        email_alerts: true,
        filters: {
          type: filters.type !== 'all' ? filters.type : null,
          propertyType: filters.propertyType !== 'all' ? filters.propertyType : null,
          area: filters.location || null,
          minPrice: filters.minPrice > 0 ? filters.minPrice : null,
          maxPrice: filters.maxPrice < 1000000 ? filters.maxPrice : null,
          minBedrooms: filters.bedrooms !== 'all' ? parseInt(filters.bedrooms) : null,
          polygon: polygon && polygon.length >= 3 ? polygon : null,
        },
      };
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/saved-searches`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({
        title: 'Search saved',
        description: polygon && polygon.length >= 3
          ? 'Your drawn area is saved — you\'ll get an alert when a new home lands inside it.'
          : 'You\'ll get an email when new properties match.',
      });
    } catch (e) {
      toast({
        title: 'Could not save',
        description: e.response?.data?.detail || e.message,
        variant: 'destructive',
      });
    } finally {
      setSavingSearch(false);
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <SEO
        title={`Property Listings — ${market.name}`}
        description={`Browse ${filteredProperties.length} verified properties for sale and rent across ${market.name} with true cost transparency and AI-powered insights.`}
        path={market.withPrefix('/listings')}
      />
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-3xl font-bold text-gray-900">Property Listings</h1>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={saveSearch}
                disabled={savingSearch}
                data-testid="save-search-btn"
              >
                {savingSearch ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Bell className="h-4 w-4 mr-2" />}
                Save search
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className="md:hidden"
              >
                <Filter className="h-5 w-5 mr-2" />
                Filters
              </Button>
            </div>
          </div>

          {/* AI natural-language search — same component as the
              homepage hero, mounted here so buyers who arrived by
              other routes can refine in plain English. */}
          <div className="mb-4">
            <AISearchBar testId="listings-ai-search" darkOnLight showExamples={false} />
          </div>

          <p className="text-gray-600">
            {filteredProperties.length} properties found
            {polygon.length >= 3 && (
              <span className="ml-2 text-xs font-semibold text-[#7042cc]">
                · filtered by drawn area ({polygon.length} pts)
              </span>
            )}
          </p>
          <Button
            type="button"
            onClick={() => {
              setShowMap((v) => {
                const next = !v;
                // Smooth-scroll to the map panel when opening on mobile
                // so it doesn't feel like nothing happened.
                if (next) {
                  setTimeout(() => {
                    document.getElementById('listings-map-panel')
                      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 60);
                }
                return next;
              });
            }}
            variant={showMap ? 'outline' : 'default'}
            size="sm"
            className={`mt-3 ${showMap
              ? ''
              : 'bg-[#7042cc] hover:bg-[#5b35a8] text-white shadow-md ring-2 ring-[#7042cc]/25 animate-pulse'}`}
            data-testid="toggle-map-view"
          >
            {showMap ? <MapIcon className="h-4 w-4 mr-1.5" /> : <Pencil className="h-4 w-4 mr-1.5" />}
            {showMap ? 'Hide map' : 'Draw your search area on the map'}
          </Button>
        </div>
      </div>

      {showMap && (
        <div id="listings-map-panel" className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <PropertyMap
              location={{ country: market.name || 'Ireland' }}
              height="380px"
              zoom={market.code === 'GB' ? 6 : 7}
              drawEnabled
              onPolygonChange={pushPolygon}
            />
            {polygon.length >= 3 && (
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <Button
                  onClick={saveSearch}
                  disabled={savingSearch}
                  size="sm"
                  className="bg-[#7042cc] hover:bg-[#5b35a8] text-white"
                  data-testid="save-drawn-area-btn"
                >
                  {savingSearch ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Bell className="h-3.5 w-3.5 mr-1.5" />}
                  Save this area + get alerts
                </Button>
                {(polygonHistory.length > 0 || polygon.length >= 3) && (
                  <Button
                    onClick={undoPolygon}
                    size="sm"
                    variant="outline"
                    data-testid="undo-polygon-btn"
                    title="Undo last drawn shape without wiping your filters"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1"><path d="M3 7v6h6"/><path d="M21 17a9 9 0 0 0-15-6.7L3 13"/></svg>
                    Undo
                  </Button>
                )}
                <span className="text-xs text-gray-500">
                  We&apos;ll email you when a new home lands inside your shape.
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* SEO-anchor intro — gives Google something unique + scannable above
          the property grid, the single biggest fix for the "Crawled,
          currently not indexed" verdict on dynamic listing pages.
          Hidden visually on mobile to keep the experience tight, but still
          present in the DOM for crawlers. */}
      <section className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-3">
            {isUK ? 'The UK\u2019s honest property search' : market.code === 'US' ? 'America\u2019s honest property search' : 'Ireland\u2019s honest property search'}
          </h2>
          <div className="grid gap-4 md:grid-cols-3 text-sm text-gray-700 leading-relaxed">
            <p>
              Every listing on lovinahome shows a true 30-year cost — purchase price plus
              {' '}{isUK ? 'stamp duty land tax' : market.code === 'US' ? 'closing costs' : 'stamp duty'},
              {' '}{market.code === 'US' ? 'title fees' : 'solicitor fees'} and total mortgage interest — so you can compare homes on what they
              actually cost you, not just the sticker. No hidden Featured ribbons inflating the rankings
              you see.
            </p>
            <p>
              Filter by {energyLabel} rating, school catchment and real-world commute time, save searches and
              get push alerts the moment a matching home is listed. Our AI sketches out a typical day
              in each layout and answers any question you have about the area before you book a viewing.
            </p>
            <p>
              {isUK
                ? 'Built for UK buyers, renters and sellers. Listings are pulled from HM Land Registry Price Paid Data and verified directly with the selling agent — including Propertymark licence checks on every agent profile so you always know who you\u2019re dealing with.'
                : market.code === 'US'
                  ? 'Built for US buyers, renters and sellers. Listings are pulled from MLS + county public records and verified directly with the selling agent — including state licence checks on every agent profile so you always know who you\u2019re dealing with.'
                  : 'Built in Ireland for Irish buyers, renters and sellers. Listings are pulled from Property Price Register data and verified directly with the selling agent — including PSRA licence checks on every agent profile so you always know who you\u2019re dealing with.'}
            </p>
          </div>
        </div>
      </section>

      {/* schema.org/ItemList structured data — explicitly tells Google
          "this page is a curated list of real estate listings" rather than
          letting it guess. Helps move /listings out of "Crawled - not
          indexed" and into the index. We cap at 30 to keep payload small. */}
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": `Property listings on lovinahome — ${market.name}`,
          "description": `Verified properties for sale and rent across ${market.name} with honest 30-year cost transparency, ${energyLabel} ratings, AI-powered insights and licensed agents.`,
          "numberOfItems": filteredProperties.length,
          "itemListElement": filteredProperties.slice(0, 30).map((p, idx) => ({
            "@type": "ListItem",
            "position": idx + 1,
            "url": `https://lovinahome.com${market.url_prefix || ''}/property/${p.id || p._id}`,
            "name": p.title || p.address || "Property listing",
          })),
        })}</script>
      </Helmet>

      {/* Recently viewed — quick re-entry to properties from this session */}
      <RecentlyViewedRail title="Continue browsing" maxItems={6} />

      {/* Personalised "For your taste" row — falls back to editorial picks */}
      <ForYourTasteRow />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Filters Sidebar */}
          <div className={`${showFilters ? 'block' : 'hidden'} md:block w-full md:w-80 shrink-0`}>
            <Card className="sticky top-20">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Filters</CardTitle>
                  <Button variant="ghost" size="sm" onClick={clearFilters}>
                    Clear All
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Type Filter */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Type
                  </label>
                  <Select
                    value={filters.type}
                    onValueChange={(value) => setFilters({ ...filters, type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="sale">For Sale</SelectItem>
                      <SelectItem value="rent">To Rent</SelectItem>
                      <SelectItem value="share">Share</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Location */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Location
                  </label>
                  <LocationCombobox
                    counties={areas}
                    value={filters.location}
                    onChange={(val) => setFilters({ ...filters, location: val })}
                    placeholder="County, area or free text…"
                  />
                </div>

                {/* Property Type */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Property Type
                  </label>
                  <Select
                    value={filters.propertyType}
                    onValueChange={(value) => setFilters({ ...filters, propertyType: value })}
                  >
                    <SelectTrigger data-testid="filter-property-type-trigger">
                      <SelectValue placeholder="Select property type" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[360px]">
                      <SelectItem value="all" data-testid="filter-property-type-option-all">
                        All property types
                      </SelectItem>
                      {groupedTypesForMode(filters.type === 'all' ? 'sale' : filters.type).map((grp) => (
                        <div key={grp.id}>
                          <div className="px-2 pt-2 pb-1 text-[11px] uppercase tracking-wide text-gray-500 font-semibold">
                            {grp.label}
                          </div>
                          {grp.items.map((t) => (
                            <SelectItem
                              key={t.slug}
                              value={t.slug}
                              data-testid={`filter-property-type-option-${t.slug}`}
                            >
                              {t.label}
                            </SelectItem>
                          ))}
                        </div>
                      ))}
                    </SelectContent>
                  </Select>
                  {/* New Home flag — toggle on top of any residential type */}
                  <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!filters.isNewHome}
                      onChange={(e) => setFilters({ ...filters, isNewHome: e.target.checked })}
                      data-testid="filter-newhome-checkbox"
                    />
                    <span>New homes only (new builds / off-plan)</span>
                  </label>
                </div>

                {/* Price Range */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Price Range: {market.formatCurrency(filters.minPrice)} - {market.formatCurrency(filters.maxPrice)}
                  </label>
                  <Slider
                    min={0}
                    max={1000000}
                    step={10000}
                    value={[filters.maxPrice]}
                    onValueChange={(value) => setFilters({ ...filters, maxPrice: value[0] })}
                    className="mt-2"
                  />
                </div>

                {/* Bedrooms */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Bedrooms
                  </label>
                  <Select
                    value={filters.bedrooms}
                    onValueChange={(value) => setFilters({ ...filters, bedrooms: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select bedrooms" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Any</SelectItem>
                      <SelectItem value="1">1+</SelectItem>
                      <SelectItem value="2">2+</SelectItem>
                      <SelectItem value="3">3+</SelectItem>
                      <SelectItem value="4">4+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Bathrooms */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Bathrooms
                  </label>
                  <Select
                    value={filters.bathrooms}
                    onValueChange={(value) => setFilters({ ...filters, bathrooms: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select bathrooms" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Any</SelectItem>
                      <SelectItem value="1">1+</SelectItem>
                      <SelectItem value="2">2+</SelectItem>
                      <SelectItem value="3">3+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* SEAI BER verification toggle */}
                <div className="pt-3 border-t border-gray-100">
                  <label
                    className="flex items-start gap-3 cursor-pointer group"
                    data-testid="filter-ber-verified"
                  >
                    <input
                      type="checkbox"
                      checked={filters.berVerified}
                      onChange={(e) => setFilters({ ...filters, berVerified: e.target.checked })}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#7042cc] focus:ring-[#7042cc]"
                      data-testid="filter-ber-verified-checkbox"
                    />
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-gray-900 group-hover:text-[#5b35a8] flex items-center gap-1.5">
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-[10px] font-bold uppercase tracking-wider">
                          ✓ Verified
                        </span>
                        {energyLabel}-verified listings only
                      </span>
                      <span className="block text-[11px] text-gray-500 mt-0.5 leading-snug">
                        Hide listings whose {energyLabel} hasn&apos;t been cross-checked against
                        {' '}{energyRegulator}.
                      </span>
                    </span>
                  </label>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Results Grid */}
          <div className="flex-1">
            {loading ? (
              <Card className="p-12 text-center">
                <p className="text-gray-600 text-lg">Loading properties...</p>
              </Card>
            ) : filteredProperties.length === 0 ? (
              <Card className="p-12 text-center">
                <p className="text-gray-600 text-lg">No properties found matching your criteria.</p>
                <Button onClick={clearFilters} className="mt-4">
                  Clear Filters
                </Button>
              </Card>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {[...filteredProperties]
                  .sort((a, b) => {
                    const order = { gold: 0, silver: 1, bronze: 2 };
                    const ra = order[resolveTier(a)] ?? 3;
                    const rb = order[resolveTier(b)] ?? 3;
                    return ra - rb;
                  })
                  .map((property) => (
                    <PropertyCard key={property._id} property={property} />
                  ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListingsPage;
